import type {SyntheticChangeEmitter} from '@github-ui/use-synthetic-change'
import {useCallback} from 'react'

const markdownPrefix = '```suggestion'
const markdownSuffix = '```'
const markdownPrefixRegex = new RegExp(`${markdownPrefix}(?:.*)\n`, 'i')
const markdownSuffixRegex = new RegExp(`${markdownSuffix}(\n|$)`)
type UsePreserveSpacingIfWithinSuggestedChangeSettings = {
  emitChange: SyntheticChangeEmitter
}

type UsePreserveSpacingIfWithinSuggestedChangeResult = {
  onKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement>
}

function findNextOccurrenceOf(regex: RegExp, input: string, cursor = 0): number {
  const str = input.substring(cursor, input.length)
  const match = regex.exec(str)
  if (match) {
    return match.index + cursor
  } else {
    return -1
  }
}

function lineAtIndex(input: string, index: number): string {
  const lines = input.slice(0, index).split('\n')
  return input.split('\n')[lines.length - 1]!
}

function leadingWhitespace(line: string): string {
  const captureLeadingWhitespaceRegex = /^(\s+)/
  const match = captureLeadingWhitespaceRegex.exec(line)
  return (match && match[0]) || ''
}

function cursorInSuggestionBlock(input: string, cursorIndex: number): boolean {
  const ranges = []
  let cursor = 0
  let startIndex = -1
  while ((startIndex = findNextOccurrenceOf(markdownPrefixRegex, input, cursor)) > -1) {
    cursor = startIndex + markdownPrefix.length
    const endIndex = findNextOccurrenceOf(markdownSuffixRegex, input, cursor)
    if (endIndex === -1) {
      // invalid diff block
      return false
    }
    cursor = endIndex + markdownSuffix.length
    ranges.push([startIndex, endIndex])
  }
  return ranges.some(range => {
    return cursorIndex > range[0]! && cursorIndex < range[1]!
  })
}

type NewSuggestionReturnType = {
  output: string
  newCursorIndex: number
}

export function buildNewSuggestionLine(textarea: HTMLTextAreaElement): NewSuggestionReturnType {
  const cursorIndex = textarea.selectionStart
  if (!cursorInSuggestionBlock(textarea.value, cursorIndex)) {
    return {output: '', newCursorIndex: 0}
  }
  const value = textarea.value
  const currentLine = lineAtIndex(value, cursorIndex)
  if (currentLine === null) {
    return {output: '', newCursorIndex: 0}
  }
  const whitespace = leadingWhitespace(currentLine)
  const lineToInsert = `\n${whitespace}`
  const newCursorIndex = cursorIndex + lineToInsert.length

  return {output: lineToInsert, newCursorIndex}
}

export const usePreserveSpacingIfWithinSuggestedChange = ({
  emitChange,
}: UsePreserveSpacingIfWithinSuggestedChangeSettings): UsePreserveSpacingIfWithinSuggestedChangeResult => {
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.key === 'Enter') {
        const textarea = event.currentTarget
        const start = textarea.selectionStart
        const end = textarea.selectionEnd
        //we do not want to do any of this logic if the user has highlighted text
        if (start !== end) return
        const {output, newCursorIndex} = buildNewSuggestionLine(textarea)
        if (output !== '') {
          event.preventDefault()
          emitChange(output, [start, start], [newCursorIndex, newCursorIndex])
        }
      }
    },
    [emitChange],
  )

  return {onKeyDown}
}
